
import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Property, InvestmentAnalysis } from '../types';
import { fetchPropertyById, fetchImagesForDeal } from '../services/ghlService';
import { analyzeInvestment } from '../services/openaiService';
import {
  ChevronLeft, MessageCircle, MapPin, Bath, BedDouble,
  CheckCircle2, Sparkles, Loader2, Image as ImageIcon,
  Maximize2, X, ChevronRight, AlertTriangle, ShieldCheck
} from 'lucide-react';

const DetailsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [property, setProperty] = useState<Property | null>(null);
  const [imageUrls, setImageUrls] = useState<string[]>([]);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [analysis, setAnalysis] = useState<InvestmentAnalysis | null>(null);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);

  // CONFIG: UPDATE YOUR WHATSAPP NUMBER HERE
  const WHATSAPP_NUMBER = "447886068208";

  useEffect(() => {
    const loadPropertyData = async () => {
      if (!id) return;
      try {
        const prop = await fetchPropertyById(id);
        if (prop) {
          setProperty(prop);
          const discovered = await fetchImagesForDeal(prop.listingRef);
          setImageUrls(discovered);
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    loadPropertyData();
  }, [id]);

  const handleAiAnalysis = async () => {
    if (!property) return;
    setAnalyzing(true);
    const result = await analyzeInvestment(property);
    setAnalysis(result);
    setAnalyzing(false);
  };

  const openLightbox = (index: number) => {
    setActiveImageIndex(index);
    setIsLightboxOpen(true);
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="flex flex-col items-center gap-4">
        <Loader2 className="animate-spin text-indigo-600" size={48} />
        <p className="text-slate-400 font-black text-[10px] uppercase tracking-[0.3em]">Preparing Property Desk</p>
      </div>
    </div>
  );

  if (!property) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="text-center p-12 bg-white rounded-[3rem] shadow-sm border border-slate-100">
        <AlertTriangle className="mx-auto text-amber-500 mb-4" size={48} />
        <h2 className="text-2xl font-black text-slate-900 mb-2">Deal Not Found</h2>
        <p className="text-slate-500 mb-8">This opportunity may have been secured or expired.</p>
        <Link to="/" className="px-8 py-3 bg-indigo-600 text-white rounded-xl font-black text-xs uppercase tracking-widest">Return to Marketplace</Link>
      </div>
    </div>
  );

  const formattedPrice = new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 }).format(property.price);
  const formattedMarketValue = new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 }).format(property.marketValue || 0);
  const profitValue = (property.marketValue || 0) - property.price;
  const formattedProfit = new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 }).format(profitValue);

  const whatsappMsg = encodeURIComponent(`Hi, I'm interested in the ${property.dealType} deal in ${property.town} [Ref: ${property.listingRef}]. Can I get more details?`);
  const whatsappUrl = `https://wa.me/${WHATSAPP_NUMBER}?text=${whatsappMsg}`;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <Link to="/" className="inline-flex items-center text-slate-400 hover:text-indigo-600 font-bold mb-8 transition-colors group text-sm uppercase tracking-widest">
        <ChevronLeft size={18} className="mr-1 group-hover:-translate-x-1 transition-transform" /> Back to Deals
      </Link>

      {/* GALLERY SECTION */}
      <div className="mb-10">
        {imageUrls.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-4 grid-rows-2 gap-3 h-[400px] md:h-[600px] rounded-[2.5rem] overflow-hidden shadow-2xl relative">
            <div
              className="md:col-span-2 md:row-span-2 relative cursor-pointer group"
              onClick={() => openLightbox(0)}
            >
              <img src={imageUrls[0]} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" alt="Property" />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />
            </div>

            {imageUrls.slice(1, 5).map((url, idx) => (
              <div
                key={idx}
                className="hidden md:block relative cursor-pointer group"
                onClick={() => openLightbox(idx + 1)}
              >
                <img src={url} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" alt="View" />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />
                {idx === 3 && imageUrls.length > 5 && (
                  <div className="absolute inset-0 bg-black/60 flex items-center justify-center text-white font-black text-xl backdrop-blur-[2px]">
                    +{imageUrls.length - 5}
                  </div>
                )}
              </div>
            ))}

            <button
              onClick={() => openLightbox(0)}
              className="absolute bottom-6 right-6 bg-white/95 backdrop-blur-md text-slate-900 px-6 py-3 rounded-2xl text-[10px] font-black uppercase tracking-widest shadow-xl border border-slate-200"
            >
              View {imageUrls.length} Gallery Photos
            </button>
          </div>
        ) : (
          <div className="h-[400px] bg-slate-50 rounded-[2.5rem] overflow-hidden border border-slate-100 shadow-inner relative group">
            <img
              src="https://assets.sitefindr.co.uk/deals/placeholders/Gemini_Generated_Image_dx19pidx19pidx19.png"
              className="w-full h-full object-cover opacity-50 grayscale hover:grayscale-0 transition-all duration-700"
              alt="Awaiting Media"
            />
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-white/40 backdrop-blur-[2px]">
              <div className="w-12 h-12 bg-white rounded-2xl flex items-center justify-center shadow-lg mb-4 text-slate-400">
                <ImageIcon size={24} />
              </div>
              <p className="font-black uppercase tracking-[0.3em] text-[10px] text-slate-500">Awaiting High-Res Media for {property.listingRef}</p>
            </div>
          </div>
        )}
      </div>

      {/* CONTENT GRID */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 mb-20">
        <div className="lg:col-span-2">
          <div className="bg-white rounded-[2.5rem] p-10 shadow-sm border border-slate-100">
            <div className="flex flex-wrap items-center gap-3 mb-6">
              <span className="bg-indigo-600 text-white px-4 py-1.5 rounded-full text-[10px] font-black uppercase tracking-[0.2em]">{property.dealType}</span>
              <div className="flex items-center text-slate-400 text-xs font-bold uppercase tracking-wider">
                <MapPin size={14} className="mr-1 text-indigo-500" /> {property.town}, {property.postcodeOutcode}
              </div>
            </div>

            <h1 className="text-4xl font-black text-slate-900 mb-8 leading-tight">{property.hook}</h1>

            <div className="flex flex-wrap items-center justify-between gap-y-10 mb-10 pb-10 border-b border-slate-100">
              <div className="flex flex-col">
                <span className="text-slate-400 text-[10px] font-black uppercase tracking-widest mb-1">Deal Price</span>
                <span className="text-2xl font-black text-slate-900">{formattedPrice}</span>
              </div>
              <div className="hidden lg:block w-px h-10 bg-slate-100" />
              <div className="flex flex-col">
                <span className="text-slate-400 text-[10px] font-black uppercase tracking-widest mb-1">End Value</span>
                <span className="text-2xl font-black text-slate-900">{formattedMarketValue}</span>
              </div>
              <div className="hidden lg:block w-px h-10 bg-slate-100" />
              <div className="flex flex-col">
                <span className="text-indigo-600 text-[10px] font-black uppercase tracking-widest mb-1">Difference</span>
                <span className="text-2xl font-black text-indigo-600">{formattedProfit}</span>
              </div>
              <div className="hidden lg:block w-px h-10 bg-slate-100" />
              <div className="flex flex-col">
                <span className="text-slate-400 text-[10px] font-black uppercase tracking-widest mb-1">Proj. Yield</span>
                <span className="text-2xl font-black text-emerald-500">{property.yieldPercent}%</span>
              </div>
              <div className="hidden lg:block w-px h-10 bg-slate-100" />
              <div className="flex flex-col">
                <span className="text-slate-400 text-[10px] font-black uppercase tracking-widest mb-1">Est. ROI</span>
                <span className="text-2xl font-black text-indigo-600">{property.roiPercent}%</span>
              </div>
              <div className="hidden lg:block w-px h-10 bg-slate-100" />
              <div className="flex flex-col">
                <span className="text-slate-400 text-[10px] font-black uppercase tracking-widest mb-1">Specs</span>
                <div className="flex items-center gap-3 text-slate-900 font-black text-lg">
                  <div className="flex items-center gap-1.5">
                    {property.bedrooms} <BedDouble size={18} className="text-slate-300" />
                  </div>
                  <span className="text-slate-200">|</span>
                  <div className="flex items-center gap-1.5">
                    {property.bathrooms} <Bath size={18} className="text-slate-300" />
                  </div>
                </div>
              </div>
            </div>

            <div className="prose prose-slate max-w-none">
              <h2 className="text-xl font-black text-slate-900 mb-4 uppercase tracking-tight">Investment Overview</h2>
              <p className="text-slate-600 leading-relaxed text-lg whitespace-pre-line">{property.description}</p>
            </div>
          </div>
        </div>

        <div className="space-y-8">
          {/* CTA Box */}
          <div className="bg-slate-900 rounded-[2.5rem] p-8 shadow-2xl text-white relative overflow-hidden">
            <div className="relative z-10">
              <div className="flex items-center gap-2 mb-4">
                <ShieldCheck size={20} className="text-indigo-400" />
                <h3 className="text-2xl font-black">Reserve Deal</h3>
              </div>
              <p className="text-slate-400 text-sm mb-8 leading-relaxed">Secure your interest in this off-market opportunity. Request the full Investor Pack via our acquisition desk.</p>

              <a
                href={whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-3 w-full py-5 bg-[#25D366] hover:bg-[#20ba59] text-white font-black rounded-2xl shadow-xl transition-all hover:-translate-y-1 active:scale-95 mb-6 uppercase tracking-widest text-xs"
              >
                <MessageCircle fill="white" size={20} /> Contact Acquisition Team
              </a>

              <div className="pt-6 border-t border-slate-800 text-center">
                <p className="text-[10px] text-slate-500 uppercase tracking-[0.3em] font-black">Ref: {property.listingRef}</p>
              </div>
            </div>
          </div>

          {/* AI Analysis */}
          <div className="bg-white rounded-[2.5rem] p-8 border border-slate-100 shadow-sm">
            {!analysis && !analyzing ? (
              <div className="text-center">
                <div className="w-12 h-12 bg-indigo-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg shadow-indigo-200">
                  <Sparkles size={24} className="text-white" />
                </div>
                <h3 className="text-lg font-black text-slate-900 mb-2">AI Investor Insight</h3>
                <p className="text-slate-500 text-xs mb-6 px-4 font-medium leading-relaxed">Instant AI risk-reward profiling based on real-time market data.</p>
                <button
                  onClick={handleAiAnalysis}
                  className="w-full py-3 bg-slate-900 text-white font-black rounded-xl hover:bg-slate-800 transition-all text-[10px] uppercase tracking-widest"
                >
                  Analyze with OpenAI®
                </button>
              </div>
            ) : analyzing ? (
              <div className="text-center py-6">
                <Loader2 className="animate-spin mx-auto mb-4 text-indigo-600" />
                <p className="text-indigo-600 text-[10px] font-black uppercase tracking-widest">Generating Insight...</p>
              </div>
            ) : analysis ? (
              <div className="animate-in fade-in slide-in-from-bottom-2 duration-500">
                <h3 className="flex items-center gap-2 text-sm font-black text-slate-900 mb-6 uppercase tracking-widest">
                  <Sparkles size={16} className="text-indigo-600" />
                  OpenAI Analysis
                </h3>
                <div className="space-y-6">
                  <p className="text-sm font-semibold leading-relaxed text-slate-700 italic">"{analysis.summary}"</p>
                  <div className="space-y-3">
                    {analysis.pros.slice(0, 3).map((pro, i) => (
                      <div key={i} className="flex items-start gap-3 text-xs text-slate-600 font-medium">
                        <CheckCircle2 size={16} className="text-emerald-500 flex-shrink-0" />
                        <span>{pro}</span>
                      </div>
                    ))}
                  </div>
                  <div className="pt-6 border-t border-slate-100">
                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Rating</span>
                    <p className="text-xl font-black text-indigo-600 mt-1 uppercase tracking-tight">{analysis.recommendation}</p>
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </div>

      {/* TERMS AND CONDITIONS SECTION */}
      <section className="mt-20 pt-16 border-t border-slate-200">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-8 h-8 bg-slate-100 rounded-lg flex items-center justify-center text-slate-900">
            <ShieldCheck size={18} />
          </div>
          <h4 className="text-[11px] font-black uppercase tracking-[0.4em] text-slate-900">Terms & Conditions • Sitefindr®</h4>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12 text-[10px] text-slate-400 font-bold leading-loose uppercase tracking-wider">
          <div className="space-y-4">
            <p className="text-slate-600 font-black">Information Accuracy</p>
            <p>
              All property details, figures, and financial projections provided by Sitefindr are for indicative purposes only. While every effort is made to ensure data integrity, Sitefindr does not guarantee the accuracy of listing information provided by third-party vendors or sourced via GHL integrations.
            </p>
          </div>
          <div className="space-y-4">
            <p className="text-slate-600 font-black">Investment Risk</p>
            <p>
              Property investment involves significant risk. Investors are responsible for conducting their own thorough due diligence (DD) on any potential acquisition. Sitefindr is not a financial advisor; we recommend seeking professional legal and tax advice prior to any transaction.
            </p>
          </div>
          <div className="space-y-4">
            <p className="text-slate-600 font-black">Sourcing & Fees</p>
            <p>
              Sitefindr operates as an off-market sourcing platform. A sourcing fee (standardly 2-3%) is typically applicable upon the successful completion of a deal. Full deal access and legal packs are subject to the execution of a Non-Disclosure Agreement (NDA).
            </p>
          </div>
        </div>

        <div className="mt-12 text-center text-[9px] font-black text-slate-300 uppercase tracking-[0.5em] py-8">
          © {new Date().getFullYear()} SITEFINDR PROPERTY SOURCING LTD. ALL RIGHTS RESERVED.
        </div>
      </section>

      {/* LIGHTBOX MODAL */}
      {isLightboxOpen && (
        <LightboxModal
          isOpen={isLightboxOpen}
          onClose={() => setIsLightboxOpen(false)}
          images={imageUrls}
          initialIndex={activeImageIndex}
        />
      )}
    </div>
  );
};

interface LightboxProps {
  isOpen: boolean;
  onClose: () => void;
  images: string[];
  initialIndex: number;
}

const LightboxModal: React.FC<LightboxProps> = ({ isOpen, onClose, images, initialIndex }) => {
  const [activeIndex, setActiveIndex] = useState(initialIndex);

  useEffect(() => {
    setActiveIndex(initialIndex);
  }, [initialIndex]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') setActiveIndex(prev => (prev > 0 ? prev - 1 : prev));
      if (e.key === 'ArrowRight') setActiveIndex(prev => (prev < images.length - 1 ? prev + 1 : prev));
    };

    if (isOpen) {
      window.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'auto';
    };
  }, [isOpen, onClose, images.length]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] bg-black/98 flex flex-col items-center justify-center p-4 backdrop-blur-sm animate-in fade-in duration-200">
      <button
        onClick={onClose}
        className="absolute top-6 right-6 text-white p-3 bg-white/10 rounded-full hover:bg-white/20 transition-all border border-white/20 z-50 group shadow-xl"
        aria-label="Close lightbox"
      >
        <X size={28} className="group-hover:scale-110 transition-transform" />
      </button>

      <div className="w-full max-w-6xl h-full flex flex-col justify-center gap-6 relative">
        <div className="relative flex-grow flex items-center justify-center">
          {activeIndex > 0 && (
            <button
              onClick={(e) => { e.stopPropagation(); setActiveIndex(activeIndex - 1); }}
              className="absolute left-4 p-4 text-white hover:bg-white/10 rounded-full transition-all z-40"
            >
              <ChevronLeft size={48} />
            </button>
          )}

          <img
            src={images[activeIndex]}
            className="max-w-full max-h-[80vh] object-contain rounded-xl shadow-2xl select-none"
            alt="Fullscreen"
          />

          {activeIndex < images.length - 1 && (
            <button
              onClick={(e) => { e.stopPropagation(); setActiveIndex(activeIndex + 1); }}
              className="absolute right-4 p-4 text-white hover:bg-white/10 rounded-full transition-all z-40"
            >
              <ChevronRight size={48} />
            </button>
          )}
        </div>

        <div className="flex justify-center gap-3 overflow-x-auto py-6 px-2 no-scrollbar w-full">
          {images.map((url, idx) => (
            <button
              key={idx}
              onClick={(e) => { e.stopPropagation(); setActiveIndex(idx); }}
              className={`flex-shrink-0 w-20 h-20 rounded-xl overflow-hidden border-2 transition-all ${activeIndex === idx ? 'border-indigo-500 scale-110 shadow-lg shadow-indigo-500/20' : 'border-transparent opacity-30 hover:opacity-100'}`}
            >
              <img src={url} className="w-full h-full object-cover" alt="" />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default DetailsPage;
